import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useEffect } from 'react';

import AdminLayout from './components/layout/AdminLayout';
import DJLayout from './components/layout/DJLayout';

import Login from './pages/Login';
import NotFound from './pages/NotFound';
import Unauthorized from './pages/Unauthorized';

import Dashboard from './components/dashboard/Dashboard';

import ShowList from './components/shows/ShowList';
import ShowForm from './components/shows/ShowForm';
import ScheduleEditor from './components/shows/ScheduleEditor';
import EpisodeList from './components/shows/EpisodeList';
import EpisodeUpload from './components/shows/EpisodeUpload';
import Programacion from './components/shows/Programacion';
import DJsList from './components/djs/DJsList';
import DJsForm from './components/djs/DJsForm';

import MediaLibrary from './components/music/MediaLibrary';
import MediaUpload from './components/music/MediaUpload';
import MediaDetail from './components/music/MediaDetail';

import FolderList from './components/music/FolderList';
import FolderForm from './components/music/FolderForm';
import FolderDetail from './components/music/FolderDetail';
import DuplicateScanner from './components/music/DuplicateScanner';

import RotationList from './components/rotations/RotationList';
import RotationEditor from './components/rotations/RotationEditor';

import PlaylistList from './components/playlists/PlaylistList';
import PlaylistEditor from './components/playlists/PlaylistEditor';
import RotationBlockList from './components/rotation-blocks';
import RotationBlockEditor from './components/rotation-blocks/RotationBlockEditor';

import AdvertiserList from './components/advertising/AdvertiserList';
import AdvertiserForm from './components/advertising/AdvertiserForm';
import CampaignList from './components/advertising/CampaignList';
import CampaignForm from './components/advertising/CampaignForm';
import SpotList from './components/advertising/SpotList';
import BreakSchedule from './components/advertising/BreakSchedule';
import AdBroadcasts from './components/advertising/AdBroadcasts';

import ServerList from './components/streaming/ServerList';
import ServerForm from './components/streaming/ServerForm';
import StreamHealth from './components/streaming/StreamHealth';

import JinglesList from './components/jingles/JinglesList';
import JinglesForm from './components/jingles/JinglesForm';
import PromosList from './components/jingles/PromosList';
import PromosForm from './components/jingles/PromosForm';

import UserList from './components/users/UserList';
import UserForm from './components/users/UserForm';

import SettingsPanel from './components/settings/SettingsPanel';
import SystemSettings from './components/settings/SystemSettings';

import AlertList from './components/alerts/AlertList';
import AlertToaster from './components/alerts/AlertToaster';
import { AlertsProvider } from './context/AlertsContext';
import Stats from './components/dashboard/Stats';

import BroadcastHistory from './components/history/BroadcastHistory';
import MusicHistory from './components/history/MusicHistory';
import AdHistory from './components/history/AdHistory';
import FallbackLog from './components/history/FallbackLog';

import PublicPlayer from './components/common/PublicPlayer';

import useWebSocket from './api/websocket';

const ADMIN_ROLES = ['admin', 'operator', 'editor', 'advertising'];
const ALLOWED_DJ_ROLES = ['admin', 'operator'];

function ProtectedLayout() {
  const { user, token, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-900">
        <div className="text-dark-400">Loading...</div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.role === 'dj') {
    return (
      <AlertsProvider>
        <DJLayout />
        <AlertToaster />
      </AlertsProvider>
    );
  }

  return (
    <AlertsProvider>
      <AdminLayout />
      <AlertToaster />
    </AlertsProvider>
  );
}

function RoleRoute({ roles, children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-900">
        <div className="text-dark-400">Loading...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/player" element={<PublicPlayer />} />

      {/* Protected routes with role-based layout */}
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Shows (shared: admin/operator/editor + DJ sees own shows) */}
        <Route path="/shows" element={<ShowList />} />
        <Route path="/shows/create" element={<RoleRoute roles={ADMIN_ROLES}>{<ShowForm />}</RoleRoute>} />
        <Route path="/shows/:id" element={<ShowForm />} />
        <Route path="/shows/:id/schedule" element={<RoleRoute roles={ADMIN_ROLES}>{<ScheduleEditor />}</RoleRoute>} />
        <Route path="/shows/:id/episodes" element={<EpisodeList />} />
        <Route path="/shows/:id/episodes/upload" element={<EpisodeUpload />} />
        <Route path="/episodes" element={<EpisodeList showAll={false} />} />
        <Route path="/episodes/upload" element={<EpisodeUpload />} />
        <Route path="/programacion" element={<RoleRoute roles={ADMIN_ROLES}>{<Programacion />}</RoleRoute>} />

        {/* DJs (admin) */}
        <Route path="/djs" element={<RoleRoute roles={['admin']}>{<DJsList />}</RoleRoute>} />
        <Route path="/djs/create" element={<RoleRoute roles={['admin']}>{<DJsForm />}</RoleRoute>} />
        <Route path="/djs/:id/edit" element={<RoleRoute roles={['admin']}>{<DJsForm editMode />}</RoleRoute>} />

        {/* Media Library (shared) */}
        <Route path="/media" element={<MediaLibrary />} />
        <Route path="/media/:id" element={<MediaDetail />} />
        <Route path="/media/upload" element={<MediaUpload />} />

        {/* Music Folders & Rotations (admin/operator) */}
        <Route path="/music/folders" element={<RoleRoute roles={ADMIN_ROLES}>{<FolderList />}</RoleRoute>} />
        <Route path="/music/folders/create" element={<RoleRoute roles={ADMIN_ROLES}>{<FolderForm />}</RoleRoute>} />
        <Route path="/music/folders/:id" element={<RoleRoute roles={ADMIN_ROLES}>{<FolderDetail />}</RoleRoute>} />
        <Route path="/music/folders/:id/edit" element={<RoleRoute roles={ADMIN_ROLES}>{<FolderForm editMode />}</RoleRoute>} />
        <Route path="/music/duplicates" element={<RoleRoute roles={ADMIN_ROLES}>{<DuplicateScanner />}</RoleRoute>} />

        <Route path="/rotations" element={<RoleRoute roles={ADMIN_ROLES}>{<RotationList />}</RoleRoute>} />
        <Route path="/rotations/create" element={<RoleRoute roles={ADMIN_ROLES}>{<RotationEditor />}</RoleRoute>} />
        <Route path="/rotations/:id" element={<RoleRoute roles={ADMIN_ROLES}>{<RotationEditor editMode />}</RoleRoute>} />

        {/* Playlists (shared) */}
        <Route path="/playlists" element={<PlaylistList />} />
        <Route path="/playlists/create" element={<PlaylistEditor />} />
        <Route path="/playlists/:id" element={<PlaylistEditor editMode />} />

        {/* Rotation Blocks (admin/operator) */}
        <Route path="/rotation-blocks" element={<RoleRoute roles={ADMIN_ROLES}>{<RotationBlockList />}</RoleRoute>} />
        <Route path="/rotation-blocks/create" element={<RoleRoute roles={ADMIN_ROLES}>{<RotationBlockEditor />}</RoleRoute>} />
        <Route path="/rotation-blocks/:id" element={<RoleRoute roles={ADMIN_ROLES}>{<RotationBlockEditor editMode />}</RoleRoute>} />

        {/* Advertising (admin/advertising) */}
        <Route path="/advertising/advertisers" element={<RoleRoute roles={ADMIN_ROLES}>{<AdvertiserList />}</RoleRoute>} />
        <Route path="/advertising/advertisers/create" element={<RoleRoute roles={ADMIN_ROLES}>{<AdvertiserForm />}</RoleRoute>} />
        <Route path="/advertising/advertisers/:id/edit" element={<RoleRoute roles={ADMIN_ROLES}>{<AdvertiserForm editMode />}</RoleRoute>} />

        <Route path="/advertising/campaigns" element={<RoleRoute roles={ADMIN_ROLES}>{<CampaignList />}</RoleRoute>} />
        <Route path="/advertising/campaigns/create" element={<RoleRoute roles={ADMIN_ROLES}>{<CampaignForm />}</RoleRoute>} />
        <Route path="/advertising/campaigns/:id/edit" element={<RoleRoute roles={ADMIN_ROLES}>{<CampaignForm editMode />}</RoleRoute>} />

        <Route path="/advertising/spots" element={<RoleRoute roles={ADMIN_ROLES}>{<SpotList />}</RoleRoute>} />
        <Route path="/advertising/breaks" element={<RoleRoute roles={ADMIN_ROLES}>{<BreakSchedule />}</RoleRoute>} />
        <Route path="/advertising/broadcasts" element={<RoleRoute roles={ADMIN_ROLES}>{<AdBroadcasts />}</RoleRoute>} />

        {/* Streaming (admin/operator) */}
        <Route path="/streaming" element={<RoleRoute roles={ADMIN_ROLES}>{<ServerList />}</RoleRoute>} />
        <Route path="/streaming/create" element={<RoleRoute roles={ADMIN_ROLES}>{<ServerForm />}</RoleRoute>} />
        <Route path="/streaming/:id/edit" element={<RoleRoute roles={ADMIN_ROLES}>{<ServerForm editMode />}</RoleRoute>} />

        {/* Jingles & Promos (admin/operator/editor) */}
        <Route path="/jingles" element={<RoleRoute roles={ADMIN_ROLES}>{<JinglesList />}</RoleRoute>} />
        <Route path="/jingles/create" element={<RoleRoute roles={ADMIN_ROLES}>{<JinglesForm />}</RoleRoute>} />
        <Route path="/jingles/:id/edit" element={<RoleRoute roles={ADMIN_ROLES}>{<JinglesForm editMode />}</RoleRoute>} />

        <Route path="/promos" element={<RoleRoute roles={ADMIN_ROLES}>{<PromosList />}</RoleRoute>} />
        <Route path="/promos/create" element={<RoleRoute roles={ADMIN_ROLES}>{<PromosForm />}</RoleRoute>} />
        <Route path="/promos/:id/edit" element={<RoleRoute roles={ADMIN_ROLES}>{<PromosForm editMode />}</RoleRoute>} />

        {/* Users (admin only) */}
        <Route path="/users" element={<RoleRoute roles={['admin']}>{<UserList />}</RoleRoute>} />
        <Route path="/users/create" element={<RoleRoute roles={['admin']}>{<UserForm />}</RoleRoute>} />
        <Route path="/users/:id/edit" element={<RoleRoute roles={['admin']}>{<UserForm editMode />}</RoleRoute>} />

        {/* Settings (admin only) */}
        <Route path="/settings" element={<RoleRoute roles={['admin']}>{<SettingsPanel />}</RoleRoute>} />
        <Route path="/settings/system" element={<RoleRoute roles={['admin']}>{<SystemSettings />}</RoleRoute>} />

        {/* Alerts (all) */}
        <Route path="/alerts" element={<AlertList />} />

        {/* Stats */}
        <Route path="/stats" element={<Stats />} />

        {/* History (all) */}
        <Route path="/history/broadcast" element={<BroadcastHistory />} />
        <Route path="/history/music" element={<MusicHistory />} />
        <Route path="/history/ads" element={<AdHistory />} />
        <Route path="/history/fallback" element={<FallbackLog />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
